import { IHeaderParams } from 'ag-grid-community';
import { ReactNode, useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ArrowDownIcon, ArrowUpIcon } from '../icons';
import { tokens } from '../theme/tokens';

const SortIcon = styled.span`
	display: flex;
	flex-shrink: 0;
	color: ${tokens.icon.tertiary};

	svg {
		width: 14px;
		height: 14px;
		display: block;
		color: inherit;
	}
`;

export interface DataGridHeaderParams extends IHeaderParams {
	/**
	 * Optional extra component rendered next to the column title (e.g. a legend,
	 * info tooltip or badge). Pass it through `headerComponentParams` on the
	 * column definition.
	 */
	extra?: ReactNode;
	/**
	 * Optional override for the column title color (e.g. to grey out a disabled
	 * column). Pass it through `headerComponentParams` on the column definition.
	 */
	titleColor?: string;
	/**
	 * Horizontal alignment of the header content. Defaults to left. Pass it
	 * through `headerComponentParams` on the column definition.
	 */
	align?: 'left' | 'center' | 'right';
}

/**
 * Default AG Grid header for the Treams `DataGrid`. It keeps the native sorting
 * behaviour (click to sort + sort indicator) and additionally allows rendering
 * an extra component in the column header via `headerComponentParams.extra`.
 * @param props The AG Grid header params, extended with an optional `extra` node.
 */
export function DataGridHeader(props: DataGridHeaderParams) {
	const { displayName, enableSorting, progressSort, column, api, extra, titleColor, align } = props;
	const [sort, setSort] = useState(column.getSort());

	useEffect(() => {
		const onSortChanged = () => setSort(column.getSort());

		// Grid-level event fires for every sort change (including columns that get
		// cleared when another column is sorted), so headers never go stale.
		api.addEventListener('sortChanged', onSortChanged);
		column.addEventListener('sortChanged', onSortChanged);
		onSortChanged();

		return () => {
			api.removeEventListener('sortChanged', onSortChanged);
			column.removeEventListener('sortChanged', onSortChanged);
		};
	}, [api, column]);

	return (
		<Flex
			alignItems="center"
			height="100%"
			width="100%"
			justifyContent={align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'}
		>
			<Flex
				alignItems="center"
				onClick={enableSorting ? (event) => progressSort(event.shiftKey) : undefined}
				style={{
					cursor: enableSorting ? 'pointer' : 'default',
					gap: tokens.gap.inline / 2,
				}}
			>
				<span style={{ color: titleColor, lineHeight: '14px' }}>{displayName}</span>
				{sort && <SortIcon>{sort === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}</SortIcon>}
			</Flex>
			{extra && (
				<Box ml="auto" flexShrink={0}>
					{extra}
				</Box>
			)}
		</Flex>
	);
}
